import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { db, withSyncMeta } from '@/offline/db';
import { enqueueSync } from '@/offline/sync';
import { useAuthStore } from '@/lib/auth-store';
import type { Incident, IncidentType, SyncStatus } from '@/types';
import { cn } from '@/lib/cn';
import { Plus, MapPin, Loader2, Trash2, UploadCloud } from 'lucide-react';
import { SectionCard, type SectionCardStatus } from '@/components/cockpit';

/**
 * DashboardPage — the home page IS the report list.
 *
 * Per the simplified-flow brief:
 *   - Single page showing posted + unposted reports, sorted by run
 *     number (newest incident # first; reverse-chronological).
 *   - One toolbar with exactly three actions: Create New Incident,
 *     Delete (selected), Post (selected).
 *   - Per-row checkbox for selection. Empty selection collapses
 *     Delete/Post out of the toolbar so the bar reads cleanly.
 *
 * Posted vs Unposted is determined by `sync_status`:
 *   posted   → ['nemsis_sent', 'neris_sent']
 *   unposted → everything else (draft, synced, nemsis_pending)
 */

const POSTED: SyncStatus[] = ['nemsis_sent', 'neris_sent'];

export function DashboardPage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [incidents, setIncidents] = useState<Incident[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  useEffect(() => { loadIncidents(); }, []);

  /**
   * One-tap create. No form, no required fields, no questions. We
   * materialize a blank incident with sane defaults (Fire + EMS
   * report shells, dispatch_time = now, current user as crew of 1,
   * empty everything else) and route the user straight to the
   * detail page where every field is editable in any order. Exactly
   * the "no input in front of Create" flow the user asked for.
   *
   * Side effect: also kicks off geolocation in the background so
   * GPS coords land on the incident as soon as the OS resolves.
   */
  async function createBlankIncident() {
    if (!user) return;
    const now = new Date();
    const incident: Incident = {
      id: crypto.randomUUID(),
      agency_id: user.agency_id,
      run_number: '',
      cad_number: null,
      // Default both Fire + EMS shells; user toggles "no medical"
      // on the detail page if it's a fire-only call.
      incident_types: ['fire', 'ems'] as IncidentType[],
      status: 'active',
      sync_status: 'draft',
      address: '',
      city: '',
      state: 'RI',
      zip: '',
      gps_lat: null,
      gps_lng: null,
      location_type: null,
      call_received_time: null,
      call_entered_time: null,
      dispatch_time: now.toISOString(),
      en_route_time: null,
      on_scene_time: null,
      at_patient_time: null,
      transport_time: null,
      at_hospital_time: null,
      transfer_care_other_service_time: null,
      available_time: null,
      back_in_quarters_time: null,
      cad_priority: null,
      cad_nature_code: null,
      cad_dispatch_notes: null,
      cad_caller_name: null,
      cad_caller_phone: null,
      cad_response_area: null,
      cad_cross_streets: null,
      crew_members: [user.id],
      mutual_aid: false,
      mutual_aid_agencies: [],
      created_by: user.id,
      created_at: now.toISOString(),
      updated_at: now.toISOString(),
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ...({ unit_id: null } as any),
    };
    await db.incidents.add(withSyncMeta(incident));
    await enqueueSync('incidents', incident.id, 'insert', incident as unknown as Record<string, unknown>);
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          await db.incidents.update(incident.id, {
            gps_lat: pos.coords.latitude,
            gps_lng: pos.coords.longitude,
          });
        },
        () => { /* GPS unavailable */ }
      );
    }
    navigate(`/incidents/${incident.id}`);
  }

  async function loadIncidents() {
    try {
      // Pull all and sort client-side by run_number desc (falls back
      // to dispatch_time when run_number is empty). Run numbers are
      // typically formatted "YYYY-MM-NNNN" so lexicographic descending
      // = reverse-chronological.
      const records = await db.incidents.toArray();
      records.sort((a, b) => {
        const aKey = a.run_number || a.dispatch_time;
        const bKey = b.run_number || b.dispatch_time;
        return bKey.localeCompare(aKey);
      });
      setIncidents(records);
    } catch {
      setIncidents([]);
    } finally {
      setLoading(false);
    }
  }

  function toggleSelect(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function bulkPost() {
    const ids = [...selected];
    if (ids.length === 0) return;
    setBusy(true);
    try {
      const now = new Date().toISOString();
      for (const id of ids) {
        const inc = incidents.find((i) => i.id === id);
        if (!inc || POSTED.includes(inc.sync_status)) continue;
        await db.incidents.update(id, {
          sync_status: 'nemsis_pending' as SyncStatus,
          updated_at: now,
        });
        await enqueueSync('incidents', id, 'update', {
          sync_status: 'nemsis_pending',
        });
      }
      setSelected(new Set());
      await loadIncidents();
    } finally {
      setBusy(false);
    }
  }

  async function bulkDelete() {
    const ids = [...selected];
    if (ids.length === 0) return;
    const ok = confirm(
      `Delete ${ids.length} ${ids.length === 1 ? 'report' : 'reports'}? This cannot be undone.`
    );
    if (!ok) return;
    setBusy(true);
    try {
      for (const id of ids) {
        await db.incidents.delete(id);
        await db.epcrs.where('incident_id').equals(id).delete();
        await enqueueSync('incidents', id, 'delete', { id });
      }
      setSelected(new Set());
      await loadIncidents();
    } finally {
      setBusy(false);
    }
  }

  const { posted, unposted } = useMemo(() => {
    const posted: Incident[] = [];
    const unposted: Incident[] = [];
    for (const i of incidents) {
      if (POSTED.includes(i.sync_status)) posted.push(i);
      else unposted.push(i);
    }
    return { posted, unposted };
  }, [incidents]);

  const selectedCount = selected.size;

  // Post-readiness check. A report is "ready to post" when the
  // bare-minimum compliance fields are populated: a run number, an
  // address, and an incident type. The Post button shows AMBER
  // until every selected row passes this check, then flips GREEN.
  // No selection at all defaults to amber too, signaling "select
  // and review before posting."
  function isPostReady(i: Incident): boolean {
    if (POSTED.includes(i.sync_status)) return true;
    return !!i.run_number?.trim() && !!i.address?.trim() && i.incident_types.length > 0;
  }
  const allSelectedReady =
    selectedCount > 0 && [...selected].every((id) => {
      const i = incidents.find((x) => x.id === id);
      return i ? isPostReady(i) : false;
    });

  return (
    <div className="w-full px-4 md:px-6 xl:px-8">
      {/* Sticky top action bar — three equal-weight buttons that
          stay attached to the top of the viewport while the
          report list scrolls beneath. The user always has Create
          / Post / Delete one tap away regardless of scroll
          position. */}
      <div className="dashboard-sticky-bar">
        <div className="flex items-baseline justify-between mb-2">
          <h1 className="text-xl font-bold tracking-tight text-ht-text">Reports</h1>
          <p className="text-xs text-ht-text-muted">
            {user?.full_name ? `${user.full_name} · ${user.cert_level}` : 'Provider'}
            {' · '}{incidents.length} total
            {selectedCount > 0 && ` · ${selectedCount} selected`}
          </p>
        </div>
        <div className="dashboard-action-bar">
          <button
            onClick={createBlankIncident}
            className="action-btn action-btn-create"
            title="Create a blank incident — fill in details on the next screen"
          >
            <Plus size={18} />
            <span>Create New Incident</span>
          </button>
          <button
            onClick={bulkPost}
            disabled={busy || selectedCount === 0}
            className={cn(
              'action-btn',
              allSelectedReady ? 'action-btn-post-ready' : 'action-btn-post-pending'
            )}
            title={
              selectedCount === 0
                ? 'Select rows below to post'
                : allSelectedReady
                  ? `Ready to post ${selectedCount} report${selectedCount === 1 ? '' : 's'}`
                  : `${selectedCount} selected — fill required fields (run #, address) before posting`
            }
          >
            <UploadCloud size={16} />
            <span>Post{selectedCount > 0 ? ` (${selectedCount})` : ''}</span>
          </button>
          <button
            onClick={bulkDelete}
            disabled={busy || selectedCount === 0}
            className="action-btn action-btn-delete"
            title={selectedCount === 0 ? 'Select rows below to delete' : `Delete ${selectedCount} selected`}
          >
            <Trash2 size={16} />
            <span>Delete{selectedCount > 0 ? ` (${selectedCount})` : ''}</span>
          </button>
        </div>
      </div>

      <div className="dashboard-content">
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="animate-spin text-ht-text-muted" size={28} />
        </div>
      ) : incidents.length === 0 ? (
        <div className="card text-center py-16 space-y-3">
          <p className="text-ht-text-muted">No reports yet.</p>
          <button onClick={createBlankIncident} className="btn-primary gap-2">
            <Plus size={18} />
            Create New Incident
          </button>
        </div>
      ) : (
        <>
          {unposted.length > 0 && (
            <Section
              label="Unposted"
              count={unposted.length}
              tone="warn"
              hint="Drafts and pending submissions."
              hasHot={unposted.some((i) => !i.run_number?.trim() || !i.address?.trim() || i.incident_types.length === 0)}
            >
              {unposted.map((i) => (
                <IncidentRow
                  key={i.id}
                  incident={i}
                  selected={selected.has(i.id)}
                  onToggleSelect={() => toggleSelect(i.id)}
                  onOpen={() => navigate(`/incidents/${i.id}`)}
                />
              ))}
            </Section>
          )}
          {posted.length > 0 && (
            <Section
              label="Posted"
              count={posted.length}
              tone="ok"
              hint="Submitted to NEMSIS / NERIS."
            >
              {posted.map((i) => (
                <IncidentRow
                  key={i.id}
                  incident={i}
                  selected={selected.has(i.id)}
                  onToggleSelect={() => toggleSelect(i.id)}
                  onOpen={() => navigate(`/incidents/${i.id}`)}
                />
              ))}
            </Section>
          )}
        </>
      )}
      </div>
    </div>
  );
}

/**
 * Section — wraps Unposted / Posted using cockpit-v2 SectionCard.
 *
 * Status threading: 'warn' tone (Unposted with at-risk runs) maps
 * to status="hot" so the cockpit-v2 left rule glows forge-red.
 * 'ok' tone (Posted) maps to status="satisfied" (sage). Default
 * Unposted with no overdue maps to "idle".
 *
 * Legacy fallback (v2 off) keeps the original tiny dot + label
 * pattern via the SectionCard fallback path — looks identical to
 * today's dashboard.
 */
function Section({
  label, count, tone, hint, children, hasHot,
}: {
  label: string;
  count: number;
  tone: 'warn' | 'ok';
  hint: string;
  children: React.ReactNode;
  /** True when 'warn' AND any row is overdue/at-risk. Drives the
   *  cockpit-v2 left-rule glow vs neutral idle. */
  hasHot?: boolean;
}) {
  const status: SectionCardStatus =
    tone === 'ok' ? 'satisfied' : hasHot ? 'hot' : 'idle';
  return (
    <SectionCard
      kicker={`${label} · ${count}`}
      subtitle={hint}
      status={status}
    >
      <div className="space-y-1.5">{children}</div>
    </SectionCard>
  );
}

/**
 * Strict 5-column grid:
 *   [ checkbox 32px | time block 64px | run# 1fr | type chips 88px | address 1.5fr ]
 *
 * Every row uses the SAME track template, so the run# / type chips /
 * address columns line up vertically down the list. The dispatch
 * time block is fixed-width so dates of varying length don't shift
 * the columns to its right. On narrow viewports (<480px) the address
 * column drops below the run# row.
 */
function IncidentRow({
  incident, selected, onToggleSelect, onOpen,
}: {
  incident: Incident;
  selected: boolean;
  onToggleSelect: () => void;
  onOpen: () => void;
}) {
  const t = new Date(incident.dispatch_time);
  return (
    <div
      className={cn(
        'card grid items-center gap-3 p-2.5 transition-colors',
        'grid-cols-[32px_64px_1fr] sm:grid-cols-[32px_64px_minmax(140px,1fr)_88px_1.5fr]',
        selected
          ? 'border-ht-border bg-ht-surface-alt/40'
          : 'hover:border-ht-border'
      )}
    >
      {/* col 1 — selection checkbox */}
      <label
        className="flex items-center justify-center cursor-pointer"
        onClick={(e) => e.stopPropagation()}
      >
        <input
          type="checkbox"
          checked={selected}
          onChange={onToggleSelect}
          className="h-4 w-4"
          aria-label={`Select report ${incident.run_number || incident.id}`}
        />
      </label>

      {/* col 2 — fixed-width time block */}
      <button
        onClick={onOpen}
        className="flex flex-col items-center justify-center bg-ht-surface-alt border border-ht-border rounded px-1 py-1 w-full"
      >
        <span className="font-mono text-sm font-bold text-ht-text leading-tight tabular-nums">
          {t.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })}
        </span>
        <span className="text-[9px] font-mono uppercase text-ht-text-muted leading-tight tabular-nums">
          {t.toLocaleDateString('en-US', { month: 'short', day: '2-digit' })}
        </span>
      </button>

      {/* col 3 — run number */}
      <button
        onClick={onOpen}
        className="text-left min-w-0"
      >
        <span className="font-mono text-sm font-bold text-ht-text truncate block">
          {incident.run_number || '— no run #'}
        </span>
      </button>

      {/* col 4 — type chips, fixed-width column so they line up */}
      <div className="hidden sm:flex flex-wrap items-center gap-1 min-w-0">
        {incident.incident_types.map((type) => (
          <span
            key={type}
            className={cn(
              'rounded-sm px-1.5 py-0 text-[10px] font-bold uppercase tracking-wider',
              type === 'ems' ? 'bg-ht-surface-alt text-ht-text-secondary border border-ht-border' :
              type === 'fire' ? 'bg-ht-critical-tint text-ht-critical border border-ht-critical' :
              'bg-ht-surface-alt text-ht-text border border-ht-border'
            )}
          >
            {type}
          </span>
        ))}
      </div>

      {/* col 5 — address */}
      <button
        onClick={onOpen}
        className="hidden sm:flex items-center gap-1 text-xs text-ht-text-secondary min-w-0 text-left"
        title={incident.address || 'No address'}
      >
        <MapPin size={11} className="shrink-0" />
        <span className="truncate">{incident.address || 'No address'}</span>
      </button>

      {/* narrow-viewport fallback row — type chips + address span all
          three columns under the run# */}
      <div className="col-span-3 sm:hidden flex flex-wrap items-center gap-1.5 -mt-1">
        {incident.incident_types.map((type) => (
          <span
            key={type}
            className={cn(
              'rounded-sm px-1.5 py-0 text-[10px] font-bold uppercase tracking-wider',
              type === 'ems' ? 'bg-ht-surface-alt text-ht-text-secondary border border-ht-border' :
              type === 'fire' ? 'bg-ht-critical-tint text-ht-critical border border-ht-critical' :
              'bg-ht-surface-alt text-ht-text border border-ht-border'
            )}
          >
            {type}
          </span>
        ))}
        <span className="text-xs text-ht-text-secondary truncate flex items-center gap-1 min-w-0">
          <MapPin size={11} className="shrink-0" />
          <span className="truncate">{incident.address || 'No address'}</span>
        </span>
      </div>
    </div>
  );
}
